import React, { useState, useContext, useEffect } from 'react';
import { Form, Input, Select, Row, Col, Button, Space, Popconfirm, Typography, Table, Tooltip } from 'antd';
import { EditOutlined, ExclamationCircleOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, SettingOutlined } from '@ant-design/icons';
import { Card } from '../../../../UI/Card/Card.component';
import { EditSerialNumberModal } from '../../../Modals/EditSerialNumberModal.component';
import { DeviceButtonGroup } from '../DeviceButtonGroup.component';
import { Status } from '../../Status/Status.component';
import { SignalStrength } from '../SignalStrength/SignalStrength.component';
import { getDeviceTitle } from '../DevicesList/DevicesList.component';
import { StationContext } from '../../../../../context/station-context';
import { DeviceForm } from '../DeviceForm.component';
import { Restricted } from '../../../../UI/Restricted.component';
import { saveStation } from '../../../../../services/StationService';
import { StationPermissionsContext } from '../../../../../pages/Dashboard/StationScreen';
import { PermissionsContext } from '../../../../../router/DashboardRoutes';
import { DeviceRestrictedInput } from '../DeviceRestrictedInput.component';
import { AAPNetworkSettingsModal } from './AAPNetworkSettingsModal';
import { useNotificationApi } from '../../../../UI/NotificationProvider';
import { EditSerialNumButton } from '../EditSerialNumButton.component';
import './AAP.css';

const { Item } = Form;
const { Option } = Select;

const EditableCell = ({editing, dataIndex, title, inputType, record, index, children, updateSubsUnit, cancel, ...restProps}) => {

    const [updatedField, setUpdatedField] = useState(false);

    const undoChanges = () => {
        setUpdatedField(false);
        cancel()
    }

    const updateField = () => {
        if (updatedField){
            updateSubsUnit(record.id)
        }
        setUpdatedField(false);
    }

    return (
        <td {...restProps}>
            {editing ? (
                <>
                    <Space>
                        <Item
                            name={dataIndex}
                            style={{
                                margin: 0,
                            }}
                            rules={[
                                {
                                    pattern: /^[^{}\/\\,]*$/, // curly braces, commas, and slashes
                                    message: "Invalid characters: { }  / \\ ,",
                                },
                                {
                                    required: true,
                                    message: `Dispenser name is required`,
                                },
                            ]}
                            >
                            <Input onChange={() => setUpdatedField(true)}/>
                        </Item>
                        
                        {updatedField &&
                            <Button onClick={() => updateField()} icon={<CheckOutlined/>}/>      
                        }

                        <Button type="secondary" onClick={() => undoChanges()} icon={<CloseOutlined/>}/>
                    </Space>
                </>
            ) : (
                children
            )}
        </td>
    );
};

export const AAP = ({stationID, device, listAAP, serialNum, signalStrength, anthemProfiles, isPanelPending, onSaveHandler, setDisplayAAPManager }) => {

    const notificationApi = useNotificationApi();
    
    const stationPermissions = useContext(StationPermissionsContext);
    const { remove: allowDelete } = stationPermissions.station;

    const userPermissions = useContext(PermissionsContext);
    const allowEdit = userPermissions.device.edit;

    const [stationState, stationDispatch] = useContext(StationContext);
	const [form] = Form.useForm();
    const [displayModal, setDisplayModal] = useState(false);
    const [displayNetworkSettings, setDisplayNetworkSettings] = useState(false);

	const MAX_STRENGTH = -60;
	const MIN_STRENGTH = -90;
	const GREEN_STRENGTH = -79;
	const ORANGE_STRENGTH = -84;

    const [formTable] = Form.useForm();
    const [data, setData] = useState(signalStrength);
    const [editingKey, setEditingKey] = useState('');

    const isEditing = (record) => record.id === editingKey;

    useEffect(() => {
        if(!isPanelPending(device)){
            setData(signalStrength);
        }
    }, [stationState?.pendingQueue, device, signalStrength]);

    useEffect(() => {
        if(!isPanelPending(device)){
            setEditingKey('');
        }
    }, [stationState?.pendingQueue])

    const edit = (record) => {
        formTable.setFieldsValue({
        name: '',
        ...record,
        });
        setEditingKey(record.id);
    };
    
    const cancel = () => {
        setEditingKey('');
    };
    
    const updateSubsUnit = async (id) => {
        try {
            const row = await formTable.validateFields();
            const aapFields = await form.validateFields();
            const updatedAAPForm = {...device, ...aapFields}

            const newData = [...data];
            const index = newData.findIndex((item) => id === item.id);

            if (index > -1) {
                const item = newData[index];

                newData.splice(index, 1, {...item, ...row});
                setData(newData);
                const updatedDevice = {...updatedAAPForm, SubscriberUnits: newData}

                stationDispatch({
                    type: 'ADD_PENDING_QUEUE', 
                    payload: updatedDevice
                })

                setEditingKey('');
            } else {
                newData.push(row);
                setData(newData);
                setEditingKey('');
            }
        } catch (errInfo) {
            // console.log('Validate Failed:', errInfo);
        }
    };


    const deleteSubscriberUnit = (record) => {
        device.SubscriberUnits.forEach((subsUnit) => {
            if (subsUnit.id === record?.id){
                Object.assign(subsUnit, {deleted: true});
                saveStation({devices: [device], dbID: stationState?.station?.id}).then(function(response){
                    if(response){
                        // stationDispatch({
                        //     type: 'REMOVE_SUBSCRIBER_UNIT', 
                        //     payload: device,
                        // });

                        const newData = data.filter(subunit => subunit.id !== record.id)
                        setData(newData);
                        setEditingKey('');
                        notificationApi.success({
                            message: 'Success!',
                            description: `Subscriber unit ${record.name} has been deleted.`,
                        });
                    }
                })
            }
        })
    }

    const columns = [
        {
            dataIndex: 'edit',
            width: 50,
            render: (_, record) => {
                return (
                    <Tooltip title='Edit'>
                        <Typography.Link 
                            disabled={editingKey !== ''} 
                            onClick={() => edit(record)}
                        >
                            <EditOutlined/>
                        </Typography.Link>
                    </Tooltip>
                );
            }, 
            hidden: !allowEdit
        }, 
        {
            title: 'Dispenser Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
			editable: true,
        },
        {
            title: 'USB Key ID',
            dataIndex: 'usbKeyID',
            key: 'usbKeyID',
            width: '10%',
        },
        {
            title: 'Signal Strength',
            dataIndex: 'signalstrength',
            key: 'signalstrength',
            width: '25%',
            render: (ss) => {
                let signal = getColor(ss);
                return <SignalStrength type={signal[0]} value={signal[1]}/>
            }
        },
        {
            title: 'Connection Time',
            dataIndex: 'uptime',
            key: 'uptime',
            width: '15%',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
			width: 85,
            render: (status) => {
                return <Row><Status status={status}/></Row>;
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',


            render: (_, record) => {
                const editable = isEditing(record);
                const btnSize = 'middle';
                return (
                    <Space align='baseline'>
                        { isPanelPending(device) ?
                            <Tooltip title="Device can't be deleted. Pending changes must be cleared.">
                                <Button size={btnSize} danger disabled={true} type="primary" icon={<DeleteOutlined/>}/>
                            </Tooltip> 
                            : 
                            <Popconfirm 
                                title='Are you sure you want to delete this subscriber unit?'
                                onConfirm={() => deleteSubscriberUnit(record)}
                                okText='Delete'
                                cancelText='Cancel'
                                okType='danger'
                                icon={<ExclamationCircleOutlined style={{color: 'var(--error-color)'}} />}
                                disabled={isPanelPending(device)}
                                >
                                <Button type='primary' danger size={btnSize} disabled={isPanelPending(device)} icon={<DeleteOutlined/>}/>
                            </Popconfirm>
                        }
					</Space>
                )
            },
            hidden: !allowEdit
        },
    ].filter(column => !column.hidden);

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        
        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
                updateSubsUnit: updateSubsUnit,
                cancel: cancel,
            }),
        };
    });
    
	const getColor = (signalstrength) => {
        // Convert to percent
        let  signal =  ((Number(signalstrength) - MIN_STRENGTH) / (MAX_STRENGTH - MIN_STRENGTH)) * 100;
        switch (true) {
            case signalstrength > GREEN_STRENGTH:
                return ['green', signalstrength];
            case signalstrength > ORANGE_STRENGTH:
                return ['orange', signalstrength];
            default:
                return ['red', 0];
        }
    }
    

    const onValuesChange = (changedValues, allValues) => {
        Object.assign(allValues, {id: device.id, status: device.status, deviceType: device.deviceType, SubscriberUnits: data});
        stationDispatch({
            type: 'ADD_PENDING_QUEUE', 
            payload: allValues
        })
    }

    const onFinish = async (values) => {
        try{
            await form.validateFields();
            await formTable.validateFields();
            onSaveHandler([device.id])
        } catch (error){
            // console.log('validation failed', error)
        }
    }

    return(
        <>
            <div className="device__AAP">
                <Col span={24}>
                    <Card bordered>
                        <DeviceForm 
                            form={form}
                            name={`AAP-${device.id}-device-form`}
                            layout="vertical"
                            colon={false}
                            initialValues={device}
                            onValuesChange={(changedValues, allValues) => onValuesChange(changedValues, allValues)}
                            onFinish={(values) => onFinish(values)}
                            >
                            <div className='form__layout--vertical-inline'>
                                <EditSerialNumButton
                                    serialNum={serialNum}
                                    devicePending={isPanelPending(device)}
                                    onClick={() => setDisplayModal(true)}
                                />
                                <DeviceRestrictedInput label="Description:" name="comment">
                                    <Input />
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput label="Firmware Version:" name="version">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                                <Restricted to='server.pci'>
                                    <Item label="Anthem Mode:" name="anthemMode">
                                        <Select placeholder="Select Anthem Mode">
                                            <Option value={true}>ON</Option>
                                            <Option value={false}>OFF</Option>
                                        </Select>
                                    </Item>
                                    <Item label="Anthem Profile:" name="anthemProfile">
                                        <Select
                                            filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                                            filterSort={(optionA, optionB) =>
                                                (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                                                }
                                            options={anthemProfiles?.map((profile) => ({
                                                label: `${profile.name} ${profile.value !== '0' ? `(${profile.value})` : '' }`, 
                                                value: profile.value, 
                                                key: profile.id,
                                                disabled: profile.disabled,
                                            }))}
                                            optionFilterProp='children'
                                            placeholder="Select Profile"
                                            showSearch
                                            />
                                    </Item>
                                </Restricted>
                            </div>
                        </DeviceForm>
                        <Restricted to='server.pci'>
                            <Space>
                                <Restricted to='device.edit'>
                                    <Button icon={<SettingOutlined/>} onClick={() => setDisplayNetworkSettings(true)}>Network Settings</Button>
                                </Restricted>
                                <DeviceButtonGroup serialNum={serialNum} status={device.status} ant winbox/>
                            </Space>
                        </Restricted>
                    </Card>
                </Col>

                {signalStrength?.length > 0 && 
                    <Col span={24} >
                        <Card bordered>
                            <Row justify='space-between' gutter={[0,12]}>
                                <Typography.Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800}}>Subscriber Units</Typography.Title>
                                <Col span={24}>
                                    <Form form={formTable} component={false}>
                                        <Table
                                            size='small'
                                            components={{
                                                body: {
                                                    cell: EditableCell,
                                                },
                                            }}
                                            dataSource={data}
                                            columns={mergedColumns}
                                            rowClassName="editable-row"
                                            pagination={{
                                                onChange: cancel,
                                            }}
                                            rowKey='id'
                                        />
                                    </Form>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                }

                {displayModal && 
                    <EditSerialNumberModal displayField={false} deviceType={getDeviceTitle(device.deviceType)} id={device.id} serialNum={device.serialNum} visible={displayModal} setVisible={setDisplayModal} />
                }
                
                {displayNetworkSettings && 
                    <AAPNetworkSettingsModal stationID={stationID} listAAP={listAAP} device={device} displayModal={displayNetworkSettings} setDisplayModal={setDisplayNetworkSettings}/>    
                }
            </div>
        </>
    );
}